/**
 * Phase 27 — static sender IDs on a container's gateway pool (user,
 * 12/10/2026: "add static sender IDs and set which sender id to a gateway or
 * to all"). One rule for the editor, the DTO and the fill: a sender ID is
 * 1–11 letters or digits (SMSWiser refuses anything else); each goes to
 * every gateway (routeIds null) or to the gateways it names; a drop sends
 * with the IDs of its gateway, and the AI writes names only where none
 * applies — or on every gateway too, when aiMode says so.
 * For Media Devoted | Created By Ciro Autiero
 */
export const DROP_GEN_SENDER_ID_MAX = 11;
/** SMSWiser takes at most this many sender names on one drop. */
export const DROP_GEN_SENDERS_PER_DROP_MAX = 50;
export const DROP_GEN_SENDER_AI_MODES = ["FALLBACK", "ALWAYS"] as const;
/** FALLBACK = AI names only on gateways with no static sender ID · ALWAYS = on every gateway, next to its IDs. */
export type DropGenSenderAiMode = (typeof DROP_GEN_SENDER_AI_MODES)[number];

export interface DropGenStaticSender {
  name: string;
  /** null = every gateway of the pool (one added later too); [] = none yet. */
  routeIds: number[] | null;
}

const SENDER_ID = /^[A-Za-z0-9]+$/;

/** Why `name` cannot be a sender ID — null when it can. */
export function senderIdProblem(name: string): string | null {
  const s = name.trim();
  if (s.length === 0) return "Type a sender ID";
  if (s.length > DROP_GEN_SENDER_ID_MAX) return `Up to ${DROP_GEN_SENDER_ID_MAX} characters — SMSWiser refuses longer sender IDs`;
  if (!SENDER_ID.test(s)) return "Letters and digits only — SMSWiser refuses spaces and symbols";
  return null;
}

export const parseSenderAiMode = (raw: unknown): DropGenSenderAiMode => (raw === "ALWAYS" ? "ALWAYS" : "FALLBACK");

const routeList = (raw: unknown): number[] | null => {
  if (!Array.isArray(raw)) return null;
  const ids = raw.filter((v): v is number => typeof v === "number" && Number.isInteger(v) && v > 0);
  return [...new Set(ids)];
};

/** Tolerant: bad names and duplicates are dropped, never thrown — the DTO refuses them at write time. */
export function parseStaticSenders(raw: unknown): DropGenStaticSender[] {
  if (!Array.isArray(raw)) return [];
  const out: DropGenStaticSender[] = [];
  for (const row of raw) {
    if (typeof row !== "object" || row === null) continue;
    const r = row as Record<string, unknown>;
    const name = typeof r.name === "string" ? r.name.trim() : "";
    if (senderIdProblem(name) !== null || out.some((s) => s.name === name)) continue;
    out.push({ name, routeIds: routeList(r.routeIds) });
    if (out.length === DROP_GEN_SENDERS_PER_DROP_MAX) break;
  }
  return out;
}

/** What a write may not carry — empty when the list is fine. */
export function staticSendersProblems(raw: unknown): string[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) return ["staticSenders must be a list"];
  if (raw.length > DROP_GEN_SENDERS_PER_DROP_MAX) return [`at most ${DROP_GEN_SENDERS_PER_DROP_MAX} sender IDs`];
  const seen = new Set<string>();
  const errs: string[] = [];
  raw.forEach((row, i) => {
    const r = typeof row === "object" && row !== null ? (row as Record<string, unknown>) : {};
    const name = typeof r.name === "string" ? r.name.trim() : "";
    const problem = senderIdProblem(name);
    if (problem) errs.push(`sender ID ${i + 1}: ${problem}`);
    else if (seen.has(name)) errs.push(`sender ID "${name}" is listed twice`);
    seen.add(name);
    if (r.routeIds !== null && r.routeIds !== undefined && routeList(r.routeIds)?.length !== (Array.isArray(r.routeIds) ? r.routeIds.length : -1))
      errs.push(`sender ID "${name || i + 1}": gateways must be route ids`);
  });
  return errs;
}

/** The static sender IDs a drop on `routeId` sends with (null route = only the "every gateway" ones). */
export const staticSendersFor = (list: readonly DropGenStaticSender[], routeId: number | null): string[] =>
  list.filter((s) => s.routeIds === null || (routeId !== null && s.routeIds.includes(routeId))).map((s) => s.name);

export interface DropGenSenderSource {
  senderGroupId: string | null;
  aiMode: DropGenSenderAiMode;
  staticSenders: readonly DropGenStaticSender[];
}

/** Where a drop's senders come from: its gateway's static IDs, and whether the AI writes names for it too. */
export function sendersForRoute(src: DropGenSenderSource, routeId: number | null): { static: string[]; ai: boolean } {
  const fixed = staticSendersFor(src.staticSenders, routeId);
  const ai = src.senderGroupId !== null && (fixed.length === 0 || src.aiMode === "ALWAYS");
  return { static: fixed, ai };
}

/** Static IDs first, then the AI's names, no duplicates, within SMSWiser's cap. */
export const mergeSenderNames = (fixed: readonly string[], ai: readonly string[]): string[] =>
  [...fixed, ...ai].filter((name, i, all) => all.findIndex((v) => v.toUpperCase() === name.toUpperCase()) === i).slice(0, DROP_GEN_SENDERS_PER_DROP_MAX);
